import { CompanyUserContextProps } from "contexts-types";
import { Columns, FilterProps, Props } from "props";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { IUserViewModel } from "viewModels";
import { useAuth } from "./auth";
import { IOrderFieldInputModel } from "inputModels";
import { CompanyUserController } from "controllers/CompanyUserController";
import { PaymentsController } from "controllers/PaymentsController";
import { IAddCreditInputModel } from "inputModels";
import { ITransactionViewModel } from "viewModels";

const PAGE_SIZE = 15;
const CompanyUserContext = createContext<CompanyUserContextProps>(
  {} as CompanyUserContextProps
);

const CompanyUserProvider: React.FC<Props> = ({ children }) => {
  const { user, logged } = useAuth();

  const [orderBy, setOrder] = useState<IOrderFieldInputModel<Columns>[]>([]);
  const [filter, setFilter] = useState<FilterProps>();
  const [companyUsers, setCompanyUsers] = useState<IUserViewModel[]>([]);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const [loadingUserCards, setLoadingUserCards] = useState<boolean>(false);
  const [loadingUserHistory, setLoadingUserHistory] = useState<boolean>(false);
  const [loadingUserTransactions, setLoadingUserTransactions] =
    useState<boolean>(false);
  const [loadingAddCredit, setLoadingAddCredit] = useState<boolean>(false);

  const {
    data: pages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    isLoading,
  } = useInfiniteQuery(
    ["companyUsers", orderBy, filter],
    ({ pageParam = 1 }) => {
      return CompanyUserController.getByCompanyId({
        orderBy,
        page: pageParam,
        take: PAGE_SIZE,
        term: filter?.term,
        filter: filter?.term ? filter?.filter : undefined,
      }).then((res) => {
        return res.data;
      });
    },
    {
      enabled: !!user?.id && logged, // Only enable when user is logged in
      refetchOnWindowFocus: false,
      getNextPageParam: (last, all) =>
        last.length < PAGE_SIZE ? undefined : all.length + 1,
    }
  );

  useEffect(() => {
    setCompanyUsers(pages?.pages.flat() ?? []);
  }, [pages]);

  const handleFetch = () => {
    return refetch();
  };
  const handleFetchNextPage = () => fetchNextPage();

  useEffect(() => {
    if (!selectedUserId) return;

    setLoadingUserCards(true);
    PaymentsController.getDashboardCardsData(selectedUserId)
      .then((res) => {
        const cards = res.data;
        setCompanyUsers((prev) =>
          prev.map((u) =>
            u.id === selectedUserId
              ? {
                  ...u,
                  financialCardsData: cards,
                  balance: cards.balance ?? u.balance,
                }
              : u
          )
        );
      })
      .catch((error) => {
        console.error("Erro ao carregar cards data:", error);
      })
      .finally(() => setLoadingUserCards(false));

    setLoadingUserHistory(true);
    PaymentsController.getFinancialHistoryData(selectedUserId)
      .then((res) => {
        const history = res.data;
        setCompanyUsers((prev) =>
          prev.map((u) =>
            u.id === selectedUserId ? { ...u, financialHistory: history } : u
          )
        );
      })
      .catch((error) => {
        console.error("Erro ao carregar financial history:", error);
      })
      .finally(() => setLoadingUserHistory(false));

    setLoadingUserTransactions(true);
    PaymentsController.getUserTransactions(selectedUserId)
      .then((res) => {
        const txs = res.data;
        setCompanyUsers((prev) =>
          prev.map((u) =>
            u.id === selectedUserId ? { ...u, transactions: txs } : u
          )
        );
      })
      .catch((error) => {
        console.error("Erro ao carregar transações:", error);
      })
      .finally(() => setLoadingUserTransactions(false));
  }, [selectedUserId]);

  const addUserCredit = async (input: IAddCreditInputModel) => {
    setLoadingAddCredit(true);
    try {
      const res = await PaymentsController.addCredit(input);
      const newTransaction: ITransactionViewModel = res.data;

      setCompanyUsers((prev) =>
        prev.map((user) =>
          user.id === input.userId
            ? {
                ...user,
                transactions: [newTransaction, ...(user.transactions ?? [])],
                balance: (user.balance ?? 0) + input.amount,
              }
            : user
        )
      );
    } catch (err) {
      console.error("Erro ao adicionar crédito:", err);
    } finally {
      setLoadingAddCredit(false);
    }
  };

  return (
    <CompanyUserContext.Provider
      value={{
        companyUsers,
        setCompanyUsers,
        orderBy,
        setOrder,
        filter,
        setFilter,
        handleFetch,
        handleFetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        selectedUserId,
        setSelectedUserId,
        loadingUserCards,
        loadingUserHistory,
        loadingUserTransactions,
        addUserCredit,
        loadingAddCredit,
      }}
    >
      {children}
    </CompanyUserContext.Provider>
  );
};

export const useCompanyUser = (): CompanyUserContextProps =>
  useContext(CompanyUserContext);

export default CompanyUserProvider;
